import { useRef } from 'react';

const EMPTY_ARRAY = [];
import {
  GLOBAL_SEARCH_COMBO_FIELD_CLASS,
  GLOBAL_SEARCH_FILTER_CHIP_CLASS,
  GLOBAL_SEARCH_FILTER_CHIP_CLOSE_CLASS,
  GLOBAL_SEARCH_KEYCAP_CLASS,
  GLOBAL_SEARCH_SURFACE_CLASS,
} from './constants.js';
import { useOperatorParser } from './useOperatorParser.js';
import { Search, X } from 'lucide-react';

/**
 * @param {object} props
 * @param {string} props.query
 * @param {(value: string) => void} props.onQueryChange
 * @param {string} [props.placeholder]
 * @param {Array<{ id: string, label: string, variant?: string }>} [props.activeFilters]
 * @param {(filterId: string) => void} [props.onRemoveFilter]
 * @param {() => void} [props.onClose]
 * @param {string} [props.escLabel]
 * @param {string} [props.cancelLabel]
 * @param {import('react').RefObject<HTMLElement|null>} [props.inputRef]
 * @param {Array<{ id: string, title: string, options: Array<{ id: string, label: string }> }>} [props.filterSections]
 * @param {(filterId: string) => void} [props.onAddFilter]
 */
const TopbarGlobalSearchHeader = ({
  query,
  onQueryChange,
  placeholder,
  activeFilters = EMPTY_ARRAY,
  onRemoveFilter,
  onClose,
  escLabel = '',
  cancelLabel = '',
  inputRef,
  filterSections = EMPTY_ARRAY,
  onAddFilter,
}) => {
  const hasFilters = activeFilters.length > 0;
  const showClearQuery = Boolean(query);
  const localInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const operatorState = useOperatorParser(query, filterSections);

  const commitOption = (optionId) => {
    onAddFilter?.(optionId);
    const prefix = query.slice(0, operatorState.matchStart).trimEnd();
    onQueryChange(prefix);
    localInputRef.current?.focus();
  };

  const handleInputKeyDown = (event) => {
    if (operatorState) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation(); // prevent modal result navigation from also firing
        dropdownRef.current?.querySelector('button')?.focus();
        return;
      }
      if (event.key === 'Escape') {
        // Close dropdown only — prevent modal from closing
        event.stopPropagation();
        onQueryChange(query.slice(0, operatorState.matchStart).trimEnd());
        return;
      }
    }
    // Backspace with empty query removes the last active chip
    if (event.key === 'Backspace' && query === '' && activeFilters.length > 0) {
      const last = activeFilters[activeFilters.length - 1];
      onRemoveFilter?.(last.id);
    }
  };

  const handleDropdownKeyDown = (event, index) => {
    const buttons = [...(dropdownRef.current?.querySelectorAll('button') ?? [])];
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      buttons[index + 1]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (index === 0) {
        localInputRef.current?.focus();
      } else {
        buttons[index - 1]?.focus();
      }
    } else if (event.key === 'Escape') {
      event.stopPropagation();
      onQueryChange(query.slice(0, operatorState.matchStart).trimEnd());
      localInputRef.current?.focus();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const opt = operatorState.suggestions[index];
      if (opt) commitOption(opt.id);
    }
  };

  return (
    <div className={['relative shrink-0 px-4 py-3 max-md:px-3 max-md:py-2.5', GLOBAL_SEARCH_SURFACE_CLASS].join(' ')}>
      <div className="flex items-center gap-3 max-md:gap-2">
        <div
          ref={inputRef}
          className={[
            'flex h-10 min-w-0 flex-1 items-center gap-1.5 rounded-lg border-[0.5px] bg-white px-3',
            GLOBAL_SEARCH_COMBO_FIELD_CLASS,
            'dark:bg-[#1a2030]',
          ].join(' ')}
        >
          <Search
            className="h-4 w-4 shrink-0 text-content-muted dark:text-gray-500"
            strokeWidth={2}
            aria-hidden
          />

          <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
            {activeFilters.map((filter) => (
              <span
                key={filter.id}
                className={[
                  'inline-flex h-5 shrink-0 items-center gap-0.5 rounded-md px-1.5 text-xs font-medium leading-4',
                  GLOBAL_SEARCH_FILTER_CHIP_CLASS,
                ].join(' ')}
              >
                <span className="whitespace-nowrap">{filter.label}</span>
                {onRemoveFilter ? (
                  <button
                    type="button"
                    aria-label={`Remove ${filter.label}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemoveFilter(filter.id);
                    }}
                    className={[
                      'inline-flex shrink-0 items-center justify-center rounded p-0.5 hover:opacity-80',
                      GLOBAL_SEARCH_FILTER_CHIP_CLOSE_CLASS,
                    ].join(' ')}
                  >
                    <X className="h-3 w-3" strokeWidth={2} />
                  </button>
                ) : null}
              </span>
            ))}

            <input
              ref={localInputRef}
              type="search"
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder={placeholder}
              className={[
                'min-w-[8rem] flex-1 border-0 bg-transparent py-0 text-sm text-content outline-none',
                'placeholder:text-content-faint dark:text-gray-100 dark:placeholder:text-gray-500',
                '[&::-webkit-search-cancel-button]:hidden',
                hasFilters ? 'pl-0.5' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            />
          </div>

          {showClearQuery ? (
            <button
              type="button"
              onClick={() => onQueryChange('')}
              className="inline-flex size-5 shrink-0 items-center justify-center rounded text-content-muted transition-colors hover:text-content dark:text-gray-500 dark:hover:text-gray-300"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          ) : null}
        </div>

        <button
          type="button"
          onClick={onClose}
          className={[
            GLOBAL_SEARCH_KEYCAP_CLASS,
            'hidden shrink-0 px-2 capitalize md:inline-flex',
          ].join(' ')}
        >
          {escLabel}
        </button>

        <button
          type="button"
          onClick={onClose}
          className={[
            'inline-flex h-8 shrink-0 items-center justify-center rounded-md px-3',
            'text-sm font-medium text-content-secondary transition-colors',
            'hover:bg-black/[0.04] dark:text-gray-300 dark:hover:bg-white/[0.06]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30',
            'md:hidden',
          ].join(' ')}
        >
          {cancelLabel}
        </button>
      </div>

      {operatorState ? (
        <div
          ref={dropdownRef}
          role="listbox"
          aria-label={`${operatorState.section.title} options`}
          className="absolute left-4 right-4 top-full z-20 mt-0.5 flex flex-col overflow-hidden rounded-lg border border-[#E5E7EB] bg-white shadow-[0_4px_16px_rgba(0,0,0,0.10)] dark:border-white/10 dark:bg-[#1a2030]"
        >
          <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#5C5C5C] dark:text-gray-500">
            {operatorState.section.title}
          </div>
          {operatorState.suggestions.map((opt, index) => (
            <button
              key={opt.id}
              type="button"
              role="option"
              aria-selected="false"
              onClick={() => commitOption(opt.id)}
              onKeyDown={(e) => handleDropdownKeyDown(e, index)}
              className="px-3 py-1.5 text-left text-sm text-[#111827] transition-colors hover:bg-[#F3F4F6] focus:bg-[#F3F4F6] focus:outline-none dark:text-gray-200 dark:hover:bg-white/[0.06] dark:focus:bg-white/[0.06]"
            >
              {opt.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default TopbarGlobalSearchHeader;
